import React, { useState, useEffect } from 'react';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  PlusCircle, TrendingDown, TrendingUp, Download, Wallet,
  FileText, ArrowUpCircle, ArrowDownCircle, RefreshCw, AlertTriangle,
} from 'lucide-react';
import client from '../../../api/apollo-client';

const ADD_COMMISSION_FUND = gql`
  mutation AddLabCommissionFund($laboratoryId: ID!, $amount: Float!, $description: String, $receivedAt: DateTime) {
    addLabCommissionFund(laboratoryId: $laboratoryId, amount: $amount, description: $description, receivedAt: $receivedAt) {
      id
      commissionFund
      commissionUsed
    }
  }
`;

const GET_COMMISSION_FUND_LOGS = gql`
  query CommissionFundLogs($laboratoryId: ID!) {
    commissionFundLogs(laboratoryId: $laboratoryId) {
      id
      amount
      description
      receivedAt
      createdAt
    }
  }
`;

const GET_LAB_TRANSACTIONS = gql`
  query TransactionsByLaboratory($laboratoryId: ID!) {
    transactionsByLaboratory(laboratoryId: $laboratoryId) {
      id
      date
      gain
      status
      product { id name }
      user { id firstName lastName email }
      pharmacy { id namePharmacy }
      label { id code promotion { id name } }
      createdAt
    }
  }
`;

const fmt = (n) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 2 }).format(n ?? 0);

const fmtDate = (d) => format(new Date(d), 'dd MMM yyyy, HH:mm', { locale: fr });

// Format a Date object to datetime-local input value (YYYY-MM-DDTHH:mm)
const toInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ─── Export helpers ───────────────────────────────────────────────────────────
function buildTimelineRows(logs, transactions) {
  const additions = logs.map((l) => ({
    type: 'addition',
    date: new Date(l.receivedAt || l.createdAt),
    createdAt: new Date(l.createdAt),
    amount: l.amount,
    description: l.description || '',
    product: '',
    user: '',
    pharmacy: '',
    promotion: '',
    ref: '',
  }));
  const deductions = transactions.map((t) => ({
    type: 'deduction',
    date: new Date(t.date || t.createdAt),
    createdAt: new Date(t.createdAt),
    amount: t.gain,
    description: '',
    product: t.product?.name || '',
    user: t.user ? `${t.user.firstName || ''} ${t.user.lastName || ''}`.trim() : '',
    pharmacy: t.pharmacy?.namePharmacy || '',
    promotion: t.label?.promotion?.name || '',
    ref: t.label?.code || t.id.slice(0, 8),
  }));
  return [...additions, ...deductions].sort((a, b) => b.date - a.date);
}

function exportCSV(rows, labName) {
  const headers = ['Type', 'Date', 'Montant (MAD)', 'Description', 'Produit', 'Vendeur', 'Pharmacie', 'Promotion', 'Référence'];
  const lines = rows.map((r) => [
    r.type === 'addition' ? 'Dotation' : 'Commission',
    format(r.date, 'yyyy-MM-dd HH:mm'),
    (r.type === 'addition' ? r.amount : -r.amount).toFixed(2),
    r.description,
    r.product,
    r.user,
    r.pharmacy,
    r.promotion,
    r.ref,
  ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'));
  const csv = '﻿' + [headers.join(';'), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dotation_${labName}_${format(new Date(), 'yyyyMMdd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportExcel(rows, labName) {
  const headers = ['Type', 'Date', 'Montant (MAD)', 'Description', 'Produit', 'Vendeur', 'Pharmacie', 'Promotion', 'Référence'];
  const toCells = (arr) => arr.map((v) => `<Cell><Data ss:Type="String">${String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;')}</Data></Cell>`).join('');
  const toRow = (arr) => `<Row>${toCells(arr)}</Row>`;
  const dataRows = rows.map((r) => toRow([
    r.type === 'addition' ? 'Dotation' : 'Commission',
    format(r.date, 'yyyy-MM-dd HH:mm'),
    (r.type === 'addition' ? r.amount : -r.amount).toFixed(2),
    r.description,
    r.product,
    r.user,
    r.pharmacy,
    r.promotion,
    r.ref,
  ]));
  const xml = `<?xml version="1.0"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Dotation"><Table>${toRow(headers)}${dataRows.join('')}</Table></Worksheet></Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dotation_${labName}_${format(new Date(), 'yyyyMMdd')}.xls`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────
const CommissionTab = ({ laboratory, refetch: refetchLab }) => {
  const [logs, setLogs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receivedAt, setReceivedAt] = useState(toInputValue(new Date()));
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all'); // all | additions | deductions

  // Accurate totals: commissionFund is the authoritative stored aggregate (always >= sum of logs)
  const logsSum = logs.reduce((sum, l) => sum + (l.amount ?? 0), 0);
  const totalDotation = Math.max(logsSum, laboratory.commissionFund ?? 0);
  const totalCommissions = transactions.reduce((sum, t) => sum + (t.gain ?? 0), 0);
  const solde = totalDotation - totalCommissions;

  const soldeColor = solde < 0
    ? 'text-red-600'
    : solde === 0
    ? 'text-amber-500'
    : 'text-green-700';
  const soldeBg = solde < 0 ? 'bg-red-50' : solde === 0 ? 'bg-amber-50' : 'bg-green-50';
  const SoldeIcon = solde < 0 ? TrendingDown : solde === 0 ? AlertTriangle : TrendingUp;

  const pct = totalDotation > 0 ? Math.min(100, (totalCommissions / totalDotation) * 100) : 0;

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, txRes] = await Promise.all([
        client.query({ query: GET_COMMISSION_FUND_LOGS, variables: { laboratoryId: laboratory.id }, fetchPolicy: 'network-only' }),
        client.query({ query: GET_LAB_TRANSACTIONS, variables: { laboratoryId: laboratory.id }, fetchPolicy: 'network-only' }),
      ]);
      setLogs(logsRes.data?.commissionFundLogs || []);
      setTransactions(txRes.data?.transactionsByLaboratory || []);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [laboratory.id]);

  const [addFund, { loading: adding }] = useMutation(ADD_COMMISSION_FUND, {
    onCompleted: () => {
      toast.success('Dotation ajoutée');
      setAmount('');
      setDescription('');
      setReceivedAt(toInputValue(new Date()));
      setShowForm(false);
      refetchLab && refetchLab();
      loadData();
    },
    onError: (e) => toast.error(e.message),
  });

  const handleAdd = () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) { toast.error('Montant invalide'); return; }
    addFund({
      variables: {
        laboratoryId: laboratory.id,
        amount: val,
        description: description.trim() || undefined,
        receivedAt: receivedAt ? new Date(receivedAt).toISOString() : undefined,
      },
    });
  };

  const timeline = buildTimelineRows(logs, transactions);
  const filtered = filter === 'additions' ? timeline.filter(r => r.type === 'addition')
    : filter === 'deductions' ? timeline.filter(r => r.type === 'deduction')
    : timeline;

  return (
    <div className="space-y-5">

      {/* ── Summary ──────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-green-600" />
            <h3 className="text-base font-semibold text-gray-800">Solde dotation commissions</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
              title="Actualiser"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setShowForm(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
            >
              <PlusCircle size={13} />
              Ajouter une dotation
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dotations reçues</p>
            <p className="text-xl font-bold text-gray-800">{fmt(totalDotation)}</p>
          </div>
          <div className="bg-red-50 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Commissions acquises</p>
            <p className="text-xl font-bold text-red-600">-{fmt(totalCommissions)}</p>
          </div>
          <div className={`${soldeBg} rounded-xl p-4 text-center`}>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Solde restant</p>
            <div className="flex items-center justify-center gap-1.5">
              <SoldeIcon size={16} className={soldeColor} />
              <p className={`text-xl font-bold ${soldeColor}`}>{fmt(solde)}</p>
            </div>
          </div>
        </div>

        {totalDotation > 0 && (
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Consommé : {fmt(totalCommissions)}</span>
              <span>{pct.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div
                className="h-2.5 rounded-full transition-all"
                style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#16a34a' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Add fund form ─────────────────────────────────────────────────── */}
      {showForm && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-green-800">Nouvelle dotation</p>
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-32">
              <label className="text-xs text-gray-600 mb-1 block">Montant (MAD) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="Ex: 1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex-[2] min-w-48">
              <label className="text-xs text-gray-600 mb-1 block">Description (optionnelle)</label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Ex: Dotation Q2 2026, Contrat #42…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex-1 min-w-48">
              <label className="text-xs text-gray-600 mb-1 block">Date de réception *</label>
              <input
                type="datetime-local"
                value={receivedAt}
                onChange={e => setReceivedAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={adding || !amount || Number(amount) <= 0}
              onClick={handleAdd}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {adding ? 'Ajout…' : 'Confirmer'}
            </button>
            <button onClick={() => { setShowForm(false); setReceivedAt(toInputValue(new Date())); }} className="text-sm text-gray-500 hover:text-gray-700">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ── Timeline ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <FileText size={15} className="text-gray-400" />
            Historique ({filtered.length} entrées)
          </h3>
          <div className="flex items-center gap-2">
            {/* Filter chips */}
            <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs font-medium">
              {[['all','Tout'], ['additions','Dotations'], ['deductions','Déductions']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setFilter(val)}
                  className={`px-3 py-1.5 transition-colors ${filter === val ? 'bg-gray-800 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Export buttons */}
            <button
              onClick={() => exportCSV(filtered, laboratory.nom)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Download size={12} />
              CSV
            </button>
            <button
              onClick={() => exportExcel(filtered, laboratory.nom)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Download size={12} />
              Excel
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">Aucune entrée</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((row, i) => (
              <div key={i} className="flex items-start gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                {/* Icon */}
                <div className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${row.type === 'addition' ? 'bg-green-100' : 'bg-red-100'}`}>
                  {row.type === 'addition'
                    ? <ArrowUpCircle size={16} className="text-green-600" />
                    : <ArrowDownCircle size={16} className="text-red-500" />
                  }
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-semibold ${row.type === 'addition' ? 'text-green-700' : 'text-red-600'}`}>
                      {row.type === 'addition' ? '+' : '-'}{fmt(Math.abs(row.amount))}
                    </span>
                    {row.type === 'addition' && (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Dotation</span>
                    )}
                    {row.type === 'deduction' && (
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full font-medium">Commission vendeur</span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 space-x-2">
                    {row.description && <span className="text-gray-700 font-medium">{row.description}</span>}
                    {row.product && <span>Produit : {row.product}</span>}
                    {row.user && <span>· Vendeur : {row.user}</span>}
                    {row.pharmacy && <span>· {row.pharmacy}</span>}
                    {row.promotion && <span>· Promo : {row.promotion}</span>}
                    {row.ref && <span className="text-gray-400">#{row.ref}</span>}
                  </div>
                  {row.type === 'addition' && row.createdAt && row.createdAt.getTime() !== row.date.getTime() && (
                    <p className="text-xs text-gray-400 mt-0.5">Enregistrée le {fmtDate(row.createdAt)}</p>
                  )}
                </div>

                {/* Date */}
                <span className="shrink-0 text-xs text-gray-400">{fmtDate(row.date)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionTab;
